import {
  type Asset,
  clipAt,
  formatTimecode,
  groupSceneLayouts,
  layoutClips,
  sceneLayoutsForFrame,
} from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { Film, ImagePlus, LayoutPanelTop, X } from "lucide-react";
import { useState } from "react";
import { orpc } from "../../lib/api";
import { cn } from "../../lib/utils";
import { useEnv } from "../env";
import { PanelTitle } from "../PanelTitle";
import { useEditor, useEditorApi } from "../store";
import { Toggle } from "../ui";
import { applyLayoutAt, fillPendingLayout } from "./commands";
import { LayoutPicture } from "./LayoutPictures";
import { useVideo, useVideoApi } from "./store";

/**
 * Screen layouts ("Bildaufteilung"): split screens, picture in picture, grids
 * and cards in a click. The clip under the playhead fills the first area;
 * clips of the tracks above that play at the same time the next ones, and
 * empty areas are filled from the media library right here (also for layouts
 * chosen in the timeline's context menu: the pending areas live in the video store).
 */

/** Videos and images of the media library for an empty area. */
function SlotMediaPicker({ onPick }: { onPick: (asset: Asset) => void }) {
  const env = useEnv();
  const [kind, setKind] = useState<"video" | "image">("video");
  const media = useQuery(
    orpc.media.list.queryOptions({ input: { orgId: env.orgId, kind, limit: 60 } }),
  );
  const items = media.data?.items.filter((asset) => asset.status === "ready") ?? [];
  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {(["video", "image"] as const).map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => setKind(entry)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100",
              kind === entry && "bg-zinc-900 text-white hover:bg-zinc-900",
            )}
          >
            {entry === "video" ? "Videos" : "Bilder"}
          </button>
        ))}
      </div>
      {media.data && items.length === 0 ? (
        <p className="flex items-center gap-2 py-3 text-xs text-zinc-500">
          {kind === "video" ? <Film className="size-4" /> : <ImagePlus className="size-4" />}
          Noch keine {kind === "video" ? "Videos" : "Bilder"} – lade sie unter „Medien“ hoch.
        </p>
      ) : null}
      <div className="grid max-h-72 grid-cols-3 gap-1.5 overflow-y-auto">
        {items.map((asset) => (
          <button
            key={asset.id}
            type="button"
            title={asset.name}
            onClick={() => onPick(asset)}
            className="relative aspect-[3/4] overflow-hidden rounded-md border border-zinc-200 bg-zinc-900 hover:ring-2 hover:ring-brand-400"
          >
            {asset.thumbnailUrl ? (
              <img
                src={asset.thumbnailUrl}
                alt=""
                loading="lazy"
                draggable={false}
                className="absolute inset-0 size-full object-cover"
              />
            ) : null}
            {asset.durationMs ? (
              <span className="absolute right-0.5 bottom-0.5 rounded bg-black/70 px-1 text-[9px] text-white">
                {formatTimecode(asset.durationMs, false)}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export function LayoutsPanel() {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const readOnly = useEditor((s) => s.readOnly);
  const width = useEditor((s) => s.doc.format.width);
  const height = useEditor((s) => s.doc.format.height);
  const timeline = useEditor((s) => s.doc.timeline);
  const time = useVideo((s) => s.time);
  const spaced = useVideo((s) => s.layoutSpaced);
  const pending = useVideo((s) => s.pendingLayout);
  const [allClips, setAllClips] = useState(false);
  const frame = { width, height };
  const entry = clipAt(layoutClips(timeline), time);

  if (readOnly) {
    return <p className="p-4 text-sm text-zinc-500">Im Ansichtsmodus lässt sich nichts ändern.</p>;
  }

  const fill = (asset: Asset) =>
    fillPendingLayout(store, video, {
      id: asset.id,
      kind: asset.kind,
      durationMs: asset.durationMs,
      name: asset.name,
    });

  return (
    <>
      <PanelTitle>Bildaufteilung</PanelTitle>
      <div className="flex-1 space-y-4 overflow-y-auto p-3">
        <p className="text-xs text-zinc-500">
          {entry
            ? `Gilt für den Clip am Abspielkopf (${formatTimecode(entry.start)} – ${formatTimecode(entry.end)}). Bereich 1 ist dieser Clip, die weiteren füllst du mit Videos oder Bildern.`
            : "Setze den Abspielkopf auf einen Clip der Hauptspur – die Aufteilung gilt für diesen Clip."}
        </p>
        <div className="space-y-2 rounded-lg bg-zinc-50 p-2.5">
          <Toggle
            checked={spaced}
            onChange={(value) => video.getState().setLayoutSpaced(value)}
            label="Mit Abstand und runden Ecken"
            description="Die Hintergrundfarbe der Seite zeigt sich zwischen den Bildern."
          />
          <Toggle
            checked={allClips}
            onChange={setAllClips}
            label="Für alle Clips der Hauptspur"
            description="Sonst nur für den Clip am Abspielkopf."
          />
        </div>
        {pending ? (
          <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50/60 p-2.5">
            <div className="flex items-start gap-2">
              <LayoutPanelTop className="mt-0.5 size-4 shrink-0 text-brand-600" />
              <p className="min-w-0 flex-1 text-xs text-zinc-700">
                <span className="font-medium text-brand-800">
                  {pending.layout}: Bereich {pending.total - pending.slots.length + 1} von{" "}
                  {pending.total}
                </span>
                <br />
                Wähle ein Video oder Bild dafür. Es liegt auf einer Spur über der Hauptspur und ist
                stumm – der Ton kommt vom Hauptclip.
              </p>
              <button
                type="button"
                title="Später"
                onClick={() => video.getState().setPendingLayout(null)}
                className="rounded p-0.5 text-zinc-500 hover:bg-white hover:text-zinc-800"
              >
                <X className="size-3.5" />
              </button>
            </div>
            <SlotMediaPicker onPick={fill} />
          </div>
        ) : null}
        {groupSceneLayouts(sceneLayoutsForFrame(frame)).map((group) => (
          <div key={group.label} className="space-y-1.5">
            <h3 className="text-[11px] font-medium text-zinc-500">{group.label}</h3>
            <div className="grid grid-cols-3 gap-1.5">
              {group.layouts.map((layout) => (
                <button
                  key={layout.id}
                  type="button"
                  onClick={() => applyLayoutAt(store, video, layout, time, { allClips })}
                  title={layout.description}
                  className="flex flex-col items-center gap-1.5 rounded-lg border border-zinc-200 px-1 pt-2 pb-1.5 hover:border-brand-400 hover:bg-brand-50"
                >
                  <LayoutPicture layout={layout} frame={frame} spaced={spaced} />
                  <span className="text-center text-[10px] leading-tight text-zinc-600">
                    {layout.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
        <p className="text-xs text-zinc-400">
          Danach lässt sich jeder Bereich auf der Fläche verschieben und an den Ecken größer ziehen;
          den Bildausschnitt änderst du rechts unter „Bildausschnitt“.
        </p>
      </div>
    </>
  );
}
